import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import startOfDay from 'date-fns/startOfDay';
import get from 'lodash/get';
import noop from 'lodash/noop';

import { useAccessToken, useSnackBar } from '@sbv-clients/ui-shared';
import {
  formatDate,
  getApiErrorMessage,
  UnwrapArray,
} from '@sbv-clients/util-misc';

import {
  Api,
  AssignSelectedOpenshiftsRequest,
  CreateAbsenceDto,
  CreateAssignmentDto,
  FacilityCacheKeys,
  ManageSchedulesApiAssignmentsDailyviewGetRequest,
  ManageSchedulesApiAssignmentsDailyviewSummaryGetRequest,
  ManageSchedulesApiAssignmentsDisplaySchedulesGetRequest,
  ManageSchedulesApiAssignmentsEmployeeAbsencePlannedGetRequest,
  ManageSchedulesApiAssignmentsEmployeeTotalBudgetsGetRequest,
  ManageSchedulesApiAssignmentsInterestedEmployeeGetRequest,
  ManageSchedulesApiAssignmentsNotifiedEmployeeGetRequest,
  ManageSchedulesApiAssignmentsScheduleCalendarAbsencesGetRequest,
  ManageSchedulesApiAssignmentsScheduleCalendarGetRequest,
  ManageSchedulesApiAssignmentsSchedulerViewsStartDateGetRequest,
  ManageSchedulesApiAssignmentsShiftBudgetsPerEmployeeScheduleGetRequest,
  ManageSchedulesApiAssignmentsV2AssignmentIdPutRequest,
  ManageSchedulesApiAssignmentsV2EmployeeScheduleGetRequest,
  ManageSchedulesApiAssignmentsV3AvailableEmployeeGetRequest,
  useApi,
} from '..';
import { ManageScheduleCacheKeys } from './util/cache-keys';

const createPlannedAbsence =
  (api: Api) => (createAbsenceDto: CreateAbsenceDto) =>
    api.manageSchedulesApi.assignmentsEmployeeAbsencePlannedPost({
      createAbsenceDto,
    });

export const useCreatePlannedAbsence = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();
  return useMutation(createPlannedAbsence(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.PlannedAbsence])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const usePlannedAbsenceGet = (
  body: ManageSchedulesApiAssignmentsEmployeeAbsencePlannedGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.PlannedAbsence, body],
    queryFn: () =>
      api.manageSchedulesApi.assignmentsEmployeeAbsencePlannedGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.employeeId),
  });
};

const deletePlannedAbsence = (api: Api) => (absenceId: number) =>
  api.manageSchedulesApi.assignmentsEmployeeAbsencePlannedAbsenceIdDelete({
    absenceId,
  });

export const useDeletePlannedAbsence = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(deletePlannedAbsence(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.PlannedAbsence])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useAbsencesGet = (
  data: ManageSchedulesApiAssignmentsScheduleCalendarAbsencesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.ScheduleCalendarAbsences, data],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsScheduleCalendarAbsencesGet(data)
        .then((res) => res.data ?? {}),
    enabled: !!data.schedulePeriodId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetAssignedEmployees = (
  openingTemplateId: number,
  scheduleDate: Date
) => {
  const api = useApi();
  const openingTemplateDate = useMemo(
    () => formatDate(scheduleDate),
    [scheduleDate]
  );
  return useQuery({
    queryKey: [
      ManageScheduleCacheKeys.AssignedEmployees,
      {
        openingTemplateId,
        openingTemplateDate,
      },
    ],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsV3AssignedEmployeeGet({
          openingTemplateId,
          openingTemplateDate,
        })
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
type AssignedEmployeesResponseType = NonNullable<
  ReturnType<typeof useGetAssignedEmployees>['data']
>;
export type AssignedEmployeeType = NonNullable<
  UnwrapArray<AssignedEmployeesResponseType['assignments']>
>;
export type AbsenceType = NonNullable<
  UnwrapArray<AssignedEmployeesResponseType['absences']>
>;
export const useGetAvailableEmployees = (
  body: ManageSchedulesApiAssignmentsV3AvailableEmployeeGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.AvailableEmployees, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsV3AvailableEmployeeGet(body)
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled:
      Boolean(body.positionId) &&
      Boolean(body.startDateTime) &&
      Boolean(body.endDateTime),
  });
};
export type AvailableEmployeesResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useGetAvailableEmployees>['data']>
>;
export type AvailableEmployeeType = NonNullable<
  UnwrapArray<AvailableEmployeesResponseType['list']>
>;
export const useGetNotifiedEmployees = (
  body: ManageSchedulesApiAssignmentsNotifiedEmployeeGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.NotifiedEmployees, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsNotifiedEmployeeGet(body)
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled:
      Boolean(body.positionId) &&
      Boolean(body.startDateTime) &&
      Boolean(body.endDateTime),
  });
};
export const useGetInterestedEmployees = (
  body: ManageSchedulesApiAssignmentsInterestedEmployeeGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.InterestedEmployees, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsInterestedEmployeeGet(body)
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled:
      Boolean(body.positionId) &&
      Boolean(body.startDateTime) &&
      Boolean(body.endDateTime),
  });
};
const createEmployeeAssignment =
  (api: Api) => (createAssignmentDto: CreateAssignmentDto) =>
    api.manageSchedulesApi.assignmentsAssignEmployeePost({
      createAssignmentDto,
    });
export const useUpdateAssignmentsCache = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.ScheduleCalendar])
      .catch(noop);
    queryClient
      .invalidateQueries([FacilityCacheKeys.EmployeeSchedules])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.DisplayViewSummary])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.EmployeeScheduleTotalBudgets])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.EmployeeScheduleShiftBudgets])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.DisplaySchedule])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.DailyView])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.DailyView1])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.AssignedEmployees])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.AvailableEmployees])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.NotifiedEmployees])
      .catch(noop);

    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.EmployeeScheduleTotalBudgets])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.EmployeeScheduleShiftBudgets])
      .catch(noop);
    queryClient
      .invalidateQueries([ManageScheduleCacheKeys.ScheduleCalendarAbsences])
      .catch(noop);
    queryClient.invalidateQueries([FacilityCacheKeys.OpenShiftsV2]).catch(noop);
    queryClient
      .invalidateQueries([FacilityCacheKeys.OpenShiftsEmployeesV2])
      .catch(noop);
  }, [queryClient]);
};
export const useCreateEmployeeAssignment = () => {
  const api = useApi();
  const updateAssignmentsCache = useUpdateAssignmentsCache();
  const { notifySuccess, notifyError } = useSnackBar();
  return useMutation(createEmployeeAssignment(api), {
    onSuccess: () => {
      updateAssignmentsCache();
      notifySuccess('Assigned successfully');
    },
    onError: async (error) => {
      const message = getApiErrorMessage(error);
      notifyError(message);
    },
  });
};

const deleteAssignment =
  (api: Api) => (body: CreateAbsenceDto & { assignmentId: number }) => {
    const { assignmentId, ...createAbsenceDto } = body;
    return api.manageSchedulesApi.assignmentsAssignmentIdDelete({
      assignmentId,
      createAbsenceDto,
    });
  };
export const useDeleteAssignment = () => {
  const api = useApi();
  const updateAssignmentsCache = useUpdateAssignmentsCache();
  const { notifyError } = useSnackBar();
  return useMutation(deleteAssignment(api), {
    onSuccess: () => {
      updateAssignmentsCache();
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};

const deleteAbsence = (api: Api) => (absenceId: number) => {
  return api.manageSchedulesApi.assignmentsEmployeeAbsenceAbsenceIdDelete({
    absenceId,
  });
};
export const useDeleteAbsence = () => {
  const api = useApi();
  const updateAssignmentsCache = useUpdateAssignmentsCache();
  const { notifyError } = useSnackBar();
  return useMutation(deleteAbsence(api), {
    onSuccess: () => {
      updateAssignmentsCache();
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};

export const useScheduleDailyViewGet = (
  apiBody: ManageSchedulesApiAssignmentsDailyviewGetRequest,
  options?: { enabled?: boolean }
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.DailyView, apiBody],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsDailyviewGet(apiBody)
        .then((response) => response?.data?.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled:
      (options?.enabled ?? true) &&
      ((isMultiFacility && Boolean(apiBody.facilityId)) || !isMultiFacility) &&
      Boolean(apiBody.departmentId),
  });
};
export type DisplayViewResponse = NonNullable<
  ReturnType<typeof useScheduleDailyViewGet>['data']
>;
export type DisplayViewUnscheduledEmployeesResponse = NonNullable<
  UnwrapArray<DisplayViewResponse['unscheduledEmployees']>
>;
export type DisplayViewAbsenceResponse = NonNullable<
  UnwrapArray<DisplayViewResponse['absences']>
>;
export type DisplayViewShiftTypesResponse = UnwrapArray<
  DisplayViewResponse['shiftTypes']
>;
export type DisplayViewUnitResponse = NonNullable<
  UnwrapArray<DisplayViewResponse['units']>
>;
export type DisplayViewPositionResponse = NonNullable<
  UnwrapArray<DisplayViewUnitResponse['positions']>
>;
export type DisplayViewOpeningInstanceResponse = NonNullable<
  UnwrapArray<DisplayViewPositionResponse['openingInstances']>
>;
export type DisplayViewAssignmentResponse = NonNullable<
  UnwrapArray<DisplayViewOpeningInstanceResponse['assignments']>
>;

export type DisplayViewAssignmentPunch = NonNullable<
  UnwrapArray<DisplayViewAssignmentResponse['punches']>
>;
export const useDisplaySchedulesGet = (
  body: ManageSchedulesApiAssignmentsDisplaySchedulesGetRequest,
  options?: {
    refetchOnMount: boolean;
  }
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.DisplaySchedule, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsDisplaySchedulesGet(body)
        .then((response) => response?.data?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled:
      ((isMultiFacility && Boolean(body.facilityId)) || !isMultiFacility) &&
      Boolean(body.startDateTime) &&
      Boolean(body.endDateTime),
    ...options,
  });
};
export type DisplayScheduleResponse = ReturnType<
  typeof useDisplaySchedulesGet
>['data'];
export type DisplaySchedulePositionResponse = NonNullable<
  UnwrapArray<DisplayScheduleResponse>
>;
export type DisplayScheduleShiftResponse = UnwrapArray<
  NonNullable<DisplaySchedulePositionResponse['shifts']>
>;
export type DisplayScheduleUnitResponse = UnwrapArray<
  NonNullable<DisplayScheduleShiftResponse['units']>
>;
export const useDailyViewSummaryGet = (
  body: ManageSchedulesApiAssignmentsDailyviewSummaryGetRequest
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.DisplayViewSummary, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsDailyviewSummaryGet(body)
        .then((response) => response?.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: (isMultiFacility && Boolean(body.facilityId)) || !isMultiFacility,
  });
};
export type DisplayViewSummaryResponse = NonNullable<
  ReturnType<typeof useDailyViewSummaryGet>['data']
>;
export type DisplayViewShiftSummaryItem = UnwrapArray<
  NonNullable<DisplayViewSummaryResponse['shiftSummary']>
>;
export type DisplayViewPositionSummaryItem = UnwrapArray<
  NonNullable<DisplayViewSummaryResponse['positionSummary']>
>;
const editEmployeeAssignment =
  (api: Api) =>
  (editAssignment: ManageSchedulesApiAssignmentsV2AssignmentIdPutRequest) => {
    return api.manageSchedulesApi.assignmentsV2AssignmentIdPut(editAssignment);
  };
export const useEditEmployeeAssignment = () => {
  const api = useApi();
  const updateAssignmentsCache = useUpdateAssignmentsCache();
  const { notifyError } = useSnackBar();
  return useMutation(editEmployeeAssignment(api), {
    onSuccess: () => {
      updateAssignmentsCache();
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};
const assignOpenShifts =
  (api: Api) =>
  (assignSelectedOpenshiftsRequest: AssignSelectedOpenshiftsRequest) =>
    api.manageSchedulesApi.assignmentsAssignOpenshiftsPost({
      assignSelectedOpenshiftsRequest,
    });
export const useAssignOpenShifts = () => {
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();
  const queryClient = useQueryClient();

  return useMutation(assignOpenShifts(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([FacilityCacheKeys.LegacyOpenShifts])
        .catch(noop);
      queryClient
        .invalidateQueries([FacilityCacheKeys.OpenShiftsV2])
        .catch(noop);
      queryClient
        .invalidateQueries([FacilityCacheKeys.OpenShiftsEmployeesV2])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.DisplaySchedule])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.DisplayViewSummary])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.AssignedEmployees])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.NotifiedEmployees])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.InterestedEmployees])
        .catch(noop);
      notifySuccess('Assigned successfully');
    },
    onError: async (error) => {
      const message = get(error, 'response.data.message', '');
      notifyError(message);
    },
  });
};
export const useGetEmployeeSchedules = (
  body: ManageSchedulesApiAssignmentsV2EmployeeScheduleGetRequest
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  const enabled = useMemo(
    () =>
      [
        !isMultiFacility || (isMultiFacility && Boolean(body.facilityId)),
        Boolean(body.departmentId),
        Boolean(body.schedulePeriodId),
      ].every(Boolean),
    [body.departmentId, body.facilityId, body.schedulePeriodId, isMultiFacility]
  );
  return useQuery({
    queryKey: [FacilityCacheKeys.EmployeeSchedules, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsV2EmployeeScheduleGet(body)
        .then((res) => res.data),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
export type EmployeeCentricViewResponseType = NonNullable<
  ReturnType<typeof useGetEmployeeSchedules>['data']
>;
export const useGetEmployeeScheduleShiftBudgets = (
  body: ManageSchedulesApiAssignmentsShiftBudgetsPerEmployeeScheduleGetRequest
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();

  const enabled = useMemo(
    () =>
      [
        !isMultiFacility || (isMultiFacility && Boolean(body.facilityId)),
        Boolean(body.departmentId),
        Boolean(body.schedulePeriodId),
      ].every(Boolean),
    [body.departmentId, body.facilityId, body.schedulePeriodId, isMultiFacility]
  );
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.EmployeeScheduleShiftBudgets, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsShiftBudgetsPerEmployeeScheduleGet(body)
        .then((res) => res.data),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
export const useGetEmployeeScheduleTotalBudgets = (
  body: ManageSchedulesApiAssignmentsEmployeeTotalBudgetsGetRequest
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  const enabled = useMemo(
    () =>
      [
        !isMultiFacility || (isMultiFacility && Boolean(body.facilityId)),
        Boolean(body.departmentId),
        Boolean(body.schedulePeriodId),
      ].every(Boolean),
    [body.departmentId, body.facilityId, body.schedulePeriodId, isMultiFacility]
  );
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.EmployeeScheduleTotalBudgets, body],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsEmployeeTotalBudgetsGet(body)
        .then((res) => res.data),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useScheduleCalendarGet = (
  data: ManageSchedulesApiAssignmentsScheduleCalendarGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.ScheduleCalendar, data],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsScheduleCalendarGet(data)
        .then((res) => {
          return res.data ?? {};
        }),
    enabled: !!data.schedulePeriodId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useSchedulerStartDateGet = (
  data: ManageSchedulesApiAssignmentsSchedulerViewsStartDateGetRequest,
  options?: { enabled?: boolean }
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.SchedulerStartDate, data.departmentId],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsSchedulerViewsStartDateGet(data)
        .then((res) => {
          return res.data ?? {};
        }),
    enabled: !!data.departmentId && (options?.enabled ?? true),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useSchedulerStartDateValueGet = (
  data: ManageSchedulesApiAssignmentsSchedulerViewsStartDateGetRequest,
  onDateResolved: (dateRange: Date) => void
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ManageScheduleCacheKeys.SchedulerStartDate, data.departmentId],
    queryFn: () =>
      api.manageSchedulesApi
        .assignmentsSchedulerViewsStartDateGet(data)
        .then((res) => {
          const startDate = res.data?.startDate as string;
          if (startDate) {
            const start = startOfDay(new Date(startDate));
            onDateResolved(start);
          }
          return res.data ?? {};
        }),
    enabled: !!data.departmentId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};
